/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { createSelector } from "@reduxjs/toolkit";
import { encode as base64Encode } from "base-64";
import { AppState } from "../../";
import { toTimespanRangeMs } from "../../helpers/helpers";
import { IfilterObj } from "../types";
import { constStr } from "../../../globals/constants";
import { deviceSiteGroupSelector, deviceRegionFilterIdsSelector, deviceConfigStatusSelector } from "../Nodes/selectors";

const EMPTY_ARRAY: string[] = [];

interface IFilterObj {
  type: string;
  operator: string;
  fieldName: string;
  value: string | number;
  predicates: {}[];
}


export const eventFilterSelector = (state: AppState) => state.eventfilter;
export const eventFilterSeveritySelector = (state: AppState) => state.eventfilter.selectedEventSeverity;
export const eventFilterManagedObjectSelector = (state: AppState) => state.eventfilter.selectedManagedObject;
export const eventFilterEventTypeSelector = (state: AppState) => state.eventfilter.selectedEventType;
export const eventFilterEventTimeSelector = (state: AppState) => state.eventfilter.selectedEventTime;
export const eventFilterEventFromTimeSelector = (state: AppState) => state.eventfilter.selectedFromEventTime;
export const eventFilterEventToTimeFilterSelector = (state: AppState) => state.eventfilter.selectedToEventTime;
export const eventFilterHostNameSelector = (state: AppState) => state.eventfilter.selectedHostName;
export const dataGridSortSelector = (state: AppState) => state.helpers.sortModel;
export const eventFilterMaintenanceModeSelector = (state: AppState) => state.eventfilter.selectedMaintenanceMode;
export const eventFilterParentKeySelector = (state: AppState) => state.eventfilter.parentKey;
const selectedGeoRegionSelector = (state: AppState) => state.admin.selectedGeoRegion.name || "";
const selectedGeoRegionIdsSelector = (state: AppState) => state.admin.selectedGeoRegionIds || EMPTY_ARRAY;
export const deviceKpiSyncSelector = (state: AppState) => state.filter.pmCollected;
export const deviceStatusSelector = (state: AppState) => state.filter.selectedDeviceStatus;
export const isDevicePageSelector = (state: AppState) => state.eventfilter.isDeviceDetailPage;
export const eventFilterLabelsSelector  = (state: AppState) =>  state.eventfilter.selectedLabels;
// Add current pathname as an input to force recomputation when route changes
let _cachedPathname = window.location.pathname;
const currentPathnameSelector = () => {
  const current = window.location.pathname;
  if (current !== _cachedPathname) _cachedPathname = current;
  return _cachedPathname;
};
export const eventFilterGeoRegionSelector = (state: AppState) => state.eventfilter.geoRegion;
const EMPTY_REGION_IDS: string[] = [];
export const eventFilterRegionIdsSelector = (state: AppState) => state.eventfilter.regionFilterIds || EMPTY_REGION_IDS;


export const urlParameterSelector = createSelector(
  deviceSiteGroupSelector,
  eventFilterLabelsSelector ,
  eventFilterSeveritySelector,
  eventFilterManagedObjectSelector,
  eventFilterEventTypeSelector,
  eventFilterEventTimeSelector,
  eventFilterEventFromTimeSelector,
  eventFilterEventToTimeFilterSelector,
  eventFilterHostNameSelector,
  eventFilterMaintenanceModeSelector,
  eventFilterParentKeySelector,
  selectedGeoRegionSelector,
  selectedGeoRegionIdsSelector,
  eventFilterRegionIdsSelector,
  deviceKpiSyncSelector,
  deviceStatusSelector,
  isDevicePageSelector,
  currentPathnameSelector,
  eventFilterGeoRegionSelector,
  deviceRegionFilterIdsSelector,
  deviceConfigStatusSelector,
  // Optional list of geo region names that can be provided via selector props
  (_state: AppState, selectedGeoRegions?: string[] | undefined) => selectedGeoRegions,
  (
    siteGroup: string,
    labels: string[] | undefined,
    severity,
    managedObject,
    eventType,
    eventTime,
    eventFromTime,
    eventToTime,
    hostName,
    maintenanceMode,
    parentKey,
    geoRegionName,
    geoRegionIds,
    regionFilterIds,
    pmCollected,
    status,
    isDeviceDetailPage,
    currentPathname,
    filterGeoRegion,
    navRegionFilterIds,
    configstatus,
    selectedGeoRegions
  ) => {
    const params = new URLSearchParams();
    const isDeviceDetail = isDeviceDetailPage;
    // Nav panel region selection takes highest priority
    const hasNavRegionFilter = !!(navRegionFilterIds && navRegionFilterIds.length > 0);
    const effectiveRegionIds = hasNavRegionFilter ? navRegionFilterIds : (regionFilterIds && regionFilterIds.length > 0) ? regionFilterIds : geoRegionIds;
    const hasExplicitRegionFilter = hasNavRegionFilter || !!(regionFilterIds && regionFilterIds.length > 0);
    const hasSelectedGeoRegions = Array.isArray(selectedGeoRegions) && selectedGeoRegions.length > 0;
    const isEnableFilter =
      severity ||
      managedObject ||
      eventType ||
      eventTime ||
      hostName ||
      maintenanceMode ||
      parentKey ||
      geoRegionName ||
      (effectiveRegionIds && effectiveRegionIds.length > 0) ||
      isDeviceDetail ||
      filterGeoRegion ||
      hasSelectedGeoRegions ||
      configstatus;
    if (isEnableFilter) {
      const paramsString: IfilterObj = { type: "OR", operator: "", fieldName: "", value: "", predicates: [] };
      const andPredicate: IFilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };

      if (!isDeviceDetail && !!siteGroup && siteGroup !== constStr.ungrouped) {
        andPredicate.predicates.push({
          type: "",
          operator: "EQUAL",
          fieldName: "siteGroupId",
          value: siteGroup,
          predicates: [{}]
        })
      }

      if (!isDeviceDetail && siteGroup === constStr.ungrouped) { andPredicate.predicates.push({ type: "", operator: "LIKE", fieldName: "siteGroupId", value: "99999999%", predicates: [{}] }); }

      if (currentPathname === "/") {
        if (hasExplicitRegionFilter) {
          andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: effectiveRegionIds.join(","), predicates: [{}] });
        } else if (hasSelectedGeoRegions) {
          andPredicate.predicates.push({
            type: "",
            operator: "IN",
            fieldName: "geoRegionId",
            value: selectedGeoRegions!.join(","),
            predicates: [{}],
          });
        } else if (filterGeoRegion && (filterGeoRegion !== "all" && filterGeoRegion.objectKey !== "")) {
          andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "geoRegionName", value: filterGeoRegion.name, predicates: [{}] });
        } else if (effectiveRegionIds && effectiveRegionIds.length > 0 && siteGroup !== constStr.ungrouped && !document.location.pathname.includes("device")) {
          andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: effectiveRegionIds.join(","), predicates: [{}] });
        }
      } else if (!document.location.pathname.includes("device")) {
        if (hasExplicitRegionFilter) {
          andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: effectiveRegionIds.join(","), predicates: [{}] });
        } else if (effectiveRegionIds && effectiveRegionIds.length > 0 && siteGroup !== constStr.ungrouped) {
          andPredicate.predicates.push({ type: "", operator: "IN", fieldName: "geoRegionId", value: effectiveRegionIds.join(","), predicates: [{}] });
        } else if (!!geoRegionName && siteGroup !== constStr.ungrouped) {
          andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "geoRegionName", value: geoRegionName, predicates: [{}] });
        }
      }

      if (severity) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "severity", value: severity, predicates: [{}] });
      }

      if (hostName) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "hostName", value: hostName, predicates: [{}] });
      }

      if (managedObject) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "managedObject", value: managedObject, predicates: [{}] });
      }

      if (labels && labels.length > 0) {
        andPredicate.predicates.push({ type: "", operator: "FIND_IN", fieldName: "labelNames", value: labels.join(","), predicates: [{}] });
      }

      if (eventType) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "eventType", value: eventType, predicates: [{}] });
      }

      if (eventTime) {
        if (eventTime === "Custom") {
          if (eventFromTime) {
            andPredicate.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: eventFromTime.toString() || "", predicates: [{}] });
          }

          if (eventToTime) {
            andPredicate.predicates.push({ type: "", operator: "LTE", fieldName: "occuredDateAndTimeMs", value: eventToTime.toString() || "", predicates: [{}] });
          }
        }
        else {
          const time = toTimespanRangeMs(eventTime);
          andPredicate.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: time?.from || "", predicates: [{}] });
        }
      }

      if (maintenanceMode) {
        andPredicate.predicates.push({ type: "", operator: "is_true", fieldName: "snMaintenanceMode", value: "", predicates: [{}] });
      }

      if (parentKey) {
        paramsString.predicates.push({ type: "", operator: "EQ", fieldName: "parentKey", value: parentKey, predicates: [{}] });
      }

      if (pmCollected) {
        andPredicate.predicates.push({ type: "", operator: "is_false", fieldName: "pmCollected", value: "", predicates: [{}] });
      }

      if (status) {
        if (status === "0" || status === 0) {
          andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: "19", predicates: [{}] });
        } else {
          andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "status", value: status, predicates: [{}] });
        }
      }

      if (configstatus) {
        andPredicate.predicates.push({ type: "", operator: "EQUAL", fieldName: "configurationStatus", value: configstatus, predicates: [{}] });
      }

      if (isDeviceDetail) {
        andPredicate.predicates.push({ type: "", operator: "EQ", fieldName: "parentKey", value: window.location.pathname?.split('/')[2], predicates: [{}] });
      }

      !isDeviceDetail && paramsString.predicates.push(andPredicate);

      // Include the "Server" OR branch for pure server events (those not tied to any device,
      // which have geoRegionId = NULL). Server-initiated events that ARE tied to a specific
      // device now have geoRegionId populated via the SQL view COALESCE, so they are already
      // caught by the main geoRegionId IN [...] branch above and correctly region-filtered.
      // The IS NULL guard prevents server events for devices in other regions from leaking
      // in when the user has selected a subset of regions.
      if (!isDeviceDetail && !hasExplicitRegionFilter) {
        const regionFilter: IfilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };
        regionFilter.predicates.push({ type: "", operator: "EQUAL", fieldName: "parentKey", value: "Server", predicates: [{}] });
        regionFilter.predicates.push({ type: "", operator: "is_null", fieldName: "geoRegionId", value: "", predicates: [{}] });

        if (severity) {
          regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "severity", value: severity, predicates: [{}] });
        }

        if (hostName) {
          regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "hostName", value: hostName, predicates: [{}] });
        }

        if (managedObject) {
          regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "managedObject", value: managedObject, predicates: [{}] });
        }

        if (labels && labels.length > 0) {
          regionFilter.predicates.push({ type: "", operator: "FIND_IN", fieldName: "labelNames", value: labels.join(","), predicates: [{}] });
        }

        if (eventType) {
          regionFilter.predicates.push({ type: "", operator: "EQ", fieldName: "eventType", value: eventType, predicates: [{}] });
        }

        if (eventTime) {
          if (eventTime === "Custom") {
            if (eventFromTime) {
              regionFilter.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: eventFromTime.toString() || "", predicates: [{}] });
            }

            if (eventToTime) {
              regionFilter.predicates.push({ type: "", operator: "LTE", fieldName: "occuredDateAndTimeMs", value: eventToTime.toString() || "", predicates: [{}] });
            }
          }
          else {
            const time = toTimespanRangeMs(eventTime);
            regionFilter.predicates.push({ type: "", operator: "GTE", fieldName: "occuredDateAndTimeMs", value: time?.from || "", predicates: [{}] });
          }
        }

        paramsString.predicates.push(regionFilter);
      }

      const base64EncodedStr = base64Encode(JSON.stringify(isDeviceDetail ? andPredicate : paramsString));
      // if (paramsString?.predicates.length > 0) {
      //   base64EncodedStr && params.append("filters", base64EncodedStr);
      // }
      if (isDeviceDetail) {
        if (andPredicate?.predicates.length > 0) {
          base64EncodedStr && params.append("filters", base64EncodedStr);
        }
      } else {
        if (paramsString?.predicates.length > 0) {
          paramsString && params.append("filters", base64EncodedStr);
        }
      }

      //Debugger: Uncomment the below line to see the base64 encoded filter.
      // console.log("Device Filter :>> ", decode(base64EncodedStr));
    }

    return params;
  }
);
